import React, { useState, useContext } from 'react';

import PropTypes from 'prop-types';
import { Grid, Button, Label } from 'semantic-ui-react';

import _get from 'lodash/get';
import _isEmpty from 'lodash/isEmpty';
import { useSelector } from 'react-redux';
import { SortableElement, SortableHandle } from 'react-sortable-hoc';

import { ContentSuggestionsModal } from 'components/modules/content/components';
import {
  SEO_TITLE_SUGGESTION_TYPE,
  SEO_DESCRIPTION_SUGGESTION_TYPE,
  SEO_TITLE_SUGGESTION_SET_TITLE,
  SEO_DESCRIPTION_SUGGESTION_SET_TITLE,
} from 'components/modules/content/constants';
import { getPageContentSuggestions } from 'components/modules/content/services/content-suggestions';
import { ContentSuggestionType } from 'components/modules/content/types';
import { isAdminUser } from 'libs/auth';
import { selectBusiness } from 'selectors';
import { selectUser } from 'selectors/user';

import Dots from '../../../../../../common/sortables/Dots';
import { isHomePage } from '../../../../../pages/services/pages';
import { PagePropType } from '../../../../../pages/types/page';
import { NavigationContext } from '../../../../context/Navigation.context';
import NavigationItemEdit from '../NavigationItemEdit';
import NavigationItemTitle from '../NavigationItemTitle';

import './NavigationItem.scss';

const propTypes = {
  page: PagePropType.isRequired,
  suggestions: PropTypes.arrayOf(ContentSuggestionType),
  hideDragHandle: PropTypes.bool,
  onMouseEnter: PropTypes.func,
  onMouseLeave: PropTypes.func,
};
const defaultProps = {
  suggestions: [],
  hideDragHandle: false,
  onMouseEnter: () => {},
  onMouseLeave: () => {},
};

const DragHandle = SortableHandle(() => <Dots size="5px" />);

export default function NavigationItem({
  page,
  suggestions,
  hideDragHandle,
  onMouseEnter,
  onMouseLeave,
}) {
  const business = useSelector(selectBusiness);
  const hasIntelligentWebsiteProduct = _get(business, 'hasIntelligentWebsiteProduct', false);

  const {
    navigation: { refreshPages },
  } = useContext(NavigationContext);
  const [isSuggestionsModalOpen, setIsSuggestionsModalOpen] = useState(false);
  const { description, seoTitle } = page;

  const user = useSelector(selectUser);
  const isAdmin = isAdminUser(user);
  const isHome = isHomePage(page);

  const displaySuggestionButton =
    !_isEmpty(suggestions) && (isAdmin || hasIntelligentWebsiteProduct);

  function onToggleSuggestionsModal() {
    setIsSuggestionsModalOpen((prev) => !prev);
  }

  async function onSaveSuggestions() {
    await refreshPages();
  }

  return (
    <div
      className="navigation-item"
      onMouseEnter={onMouseEnter}
      onTouchStart={onMouseEnter}
      onMouseLeave={onMouseLeave}
      onTouchEnd={onMouseLeave}
    >
      <Grid columns={4} stackable>
        <Grid.Column width={1} textAlign="center" verticalAlign="middle">
          {!isHome && !hideDragHandle && <DragHandle />}
        </Grid.Column>
        <Grid.Column width={6} className="title-column" verticalAlign="middle">
          <NavigationItemTitle page={page} />
        </Grid.Column>
        <Grid.Column width={6} className="description-column" verticalAlign="middle">
          <div className="description"> {description}</div>
        </Grid.Column>
        <Grid.Column
          width={2}
          className="suggestions-column"
          textAlign="right"
          verticalAlign="middle"
        >
          {displaySuggestionButton && (
            <>
              <Button
                primary
                basic
                size="tiny"
                onClick={onToggleSuggestionsModal}
                className="suggestions-button"
              >
                Suggestions
                <Label color="red" floating circular>
                  {suggestions.length}
                </Label>
              </Button>
              <ContentSuggestionsModal
                suggestionSets={[
                  {
                    originalContent: { content: seoTitle, type: 'text' },
                    suggestions: getPageContentSuggestions(
                      suggestions,
                      page,
                      SEO_TITLE_SUGGESTION_TYPE,
                    ),
                    setName: SEO_TITLE_SUGGESTION_SET_TITLE,
                  },
                  {
                    originalContent: { content: description, type: 'markdown' },
                    suggestions: getPageContentSuggestions(
                      suggestions,
                      page,
                      SEO_DESCRIPTION_SUGGESTION_TYPE,
                    ),
                    setName: SEO_DESCRIPTION_SUGGESTION_SET_TITLE,
                  },
                ]}
                onSave={onSaveSuggestions}
                onClose={onToggleSuggestionsModal}
                open={isSuggestionsModalOpen}
              />
            </>
          )}
        </Grid.Column>
        <Grid.Column width={1} textAlign="center" verticalAlign="middle">
          <NavigationItemEdit page={page} />
        </Grid.Column>
      </Grid>
    </div>
  );
}

NavigationItem.propTypes = propTypes;
NavigationItem.defaultProps = defaultProps;

const SortableNavigationItem = SortableElement(NavigationItem);
export { SortableNavigationItem };
